import _memoize from 'lodash/memoize';
import { makeThemeColor, PRIMARY, TERTIARY } from '@tornado-rdk/theme';
import { FULL_HD, HD } from '@bedrock-rdk/constants';
import { FONT, getFont } from '@tornado-rdk/typography';
import { cacheResolver, makeDefaultStylingContext } from '../tornado/utils/style';
import { identity, SCREEN_HEIGHT, SCREEN_WIDTH } from '../tornado/utils/sizes';
import {
  ITERATION_TOTAL_DURATION,
  BACKGROUND_IDLE_SUSTAIN,
  LABEL_APPEAR_DURATION,
  LABEL_APPEAR_DELAY,
} from './screenSaver.constants';

export const defaultContext = makeDefaultStylingContext();

export const colors = {
  background: makeThemeColor(TERTIARY),
  title: makeThemeColor(PRIMARY),
  details: makeThemeColor(PRIMARY, 0.5),
  gradientFill: makeThemeColor(TERTIARY),
  transparent: makeThemeColor(TERTIARY, 0),
};

// Order is important
export const BACKGROUND_STYLES = [
  // Animation zoomIn from right top corner
  {
    pivot: 0,
    zoom: {
      zoomStart: 1,
      zoomEnd: 1.11,
    },
  },
  // Animation zoomIn from left bottom corner
  {
    pivot: 1,
    zoom: {
      zoomStart: 1,
      zoomEnd: 1.06,
    },
  },
  // Animation zoomOut from center
  {
    zoom: {
      zoomStart: 1.25,
      zoomEnd: 1,
    },
  },
  // Animation zoomIn from left bottom corner
  {
    pivot: 1,
    zoom: {
      zoomStart: 1,
      zoomEnd: 1.18,
    },
  },
  // Animation zoomIn from center
  {
    zoom: {
      zoomStart: 1,
      zoomEnd: 1.12,
    },
  },
];

export const PROGRAM_LOGO_SIZE = {
  [HD]: {
    w: 251,
    h: 157,
  },
  [FULL_HD]: {
    w: 382,
    h: 215,
  },
};

export const PROGRAM_LOGO_OFFSET = {
  [HD]: {
    x: 64,
    y: 332,
  },
  [FULL_HD]: {
    x: 64,
    y: 432,
  },
};

export const LOGO_HEIGHT = {
  [HD]: 72,
  [FULL_HD]: 96,
};

export const LOGO_X_OFFSET = {
  [HD]: 64,
  [FULL_HD]: 64,
};

export const LOGO_Y_OFFSET = {
  [HD]: 32,
  [FULL_HD]: 32,
};

export const DESCRIPTION_START_X_OFFSET = {
  [HD]: 28,
  [FULL_HD]: 28,
};

export const DESCRIPTION_X_OFFSET = {
  [HD]: 64,
  [FULL_HD]: 64,
};

export const DESCRIPTION_BOTTOM_MARGIN = {
  [HD]: 58,
  [FULL_HD]: 58,
};

export const getInitialStyle = _memoize(({ screenSize, colorMode } = defaultContext) => {
  const titleFont = getFont(FONT.OSLO_3, screenSize);
  const detailsFont = getFont(FONT.KINSHASA_2, screenSize);

  return {
    alpha: 0,
    color: colors.background[colorMode],
    h: SCREEN_HEIGHT[screenSize],
    w: SCREEN_WIDTH[screenSize],
    BackgroundContainer: {
      w: identity,
      h: identity,
    },
    Gradient: {
      rect: true,
      w: identity,
      h: identity,
      colorBl: colors.gradientFill[colorMode],
      colorUl: colors.gradientFill[colorMode],
      colorUr: colors.transparent[colorMode],
      colorBr: colors.transparent[colorMode],
    },
    Logo: {
      x: LOGO_X_OFFSET[screenSize],
      y: LOGO_Y_OFFSET[screenSize],
      textureScale: 1.5,
      h: LOGO_HEIGHT[screenSize],
    },
    LogoContainer: {
      x: PROGRAM_LOGO_OFFSET[screenSize].x,
      y: SCREEN_HEIGHT[screenSize] - PROGRAM_LOGO_OFFSET[screenSize].y,
    },
    Label: {
      x: DESCRIPTION_START_X_OFFSET[screenSize],
      y: SCREEN_HEIGHT[screenSize] - DESCRIPTION_BOTTOM_MARGIN[screenSize],
      mountY: 1,
      flex: { direction: 'column' },
      Title: {
        text: {
          textColor: colors.title[colorMode],
          ...titleFont,
        },
      },
      Details: {
        text: {
          textColor: colors.details[colorMode],
          ...detailsFont,
        },
      },
    },
  };
}, cacheResolver);

export const getDescriptionAppearAnimStyle = _memoize((context = defaultContext) => {
  const { screenSize } = context;

  return {
    duration: LABEL_APPEAR_DURATION,
    repeat: 0,
    stopMethod: 'immediate',
    delay: LABEL_APPEAR_DELAY,
    actions: [
      {
        p: 'x',
        v: {
          0: { v: DESCRIPTION_START_X_OFFSET[screenSize], s: 0.1 },
          1: { v: DESCRIPTION_X_OFFSET[screenSize], sm: 1 },
        },
      },
    ],
  };
}, cacheResolver);

export const getProgramLogoStyle = _memoize((context = defaultContext) => {
  const { screenSize } = context;
  const { w, h } = PROGRAM_LOGO_SIZE[screenSize];

  return {
    w,
    h,
  };
}, cacheResolver);

export const getDummyAnimStyle = duration => ({
  duration,
  repeat: 0,
  stopMethod: 'immediate',
});

export const getFadeAnimStyle = (direction, duration, delay = 0) => ({
  duration,
  repeat: 0,
  delay,
  stopMethod: 'immediate',
  actions: [{ p: 'alpha', v: { 0: Number(direction !== 0), 1: Number(direction === 0) } }],
});

export const getBackgroundIdleAnimStyle = _memoize(
  ({ zoomStart, zoomEnd }) => ({
    duration: ITERATION_TOTAL_DURATION + BACKGROUND_IDLE_SUSTAIN,
    repeat: 0,
    stopMethod: 'immediate',
    actions: [{ p: 'scale', v: { 0: { v: zoomStart, sm: 0 }, 1: { v: zoomEnd, sm: 0 } } }],
  }),
  cacheResolver,
);
